/**
 * 広域マスタ CSV エクスポート共通ユーティリティ (spec AC-012 / AC-012b、
 * `tmp/writing-design-doc/occto-master-csv-export.md` §5「CSV 生成仕様」準拠)。
 *
 * List RPC 取得済みデータ (= 現在のフィルタ適用後の表示行) から BOM 付き
 * UTF-8 の CSV 文字列を組み立て (`buildCsvContent`)、Blob ダウンロードを
 * 実行する (`downloadCsv`)。 backend / proto の変更を伴わないフロントエンド
 * 完結の実装 (design doc §4「実行場所」決定)。
 *
 * TODO(#3794): `docs/specs/admin/master/occto-master/index.md` の P0
 * 有効期間方針にある「連携データへの反映」が ①OCCTO 提出 XML への適用開始日
 * 反映 / ②CSV エクスポートへの適用開始日・適用終了日 2 列追加 / ③計画提出時に
 * 失効マスタを選択肢から除外、のいずれを指すか未確定 (PDM 確認待ち、
 * `tmp/writing-design-doc/occto-master-valid-period-6tabs.md` §9 残論点)。
 * 本 PR は②「CSV への適用期間カラム追加」を明示的にスコープ外としている
 * (同設計書 §2「含まない」、issue #2926 / #2843 の領分)。②に決した場合は
 * 各タブの `models/csvColumns.ts` に validFrom / validTo 列を追加する。
 */
import { escapeCsvCell } from '~/libs/csv'
import { downloadBlob } from '~/libs/downloadBlob'
import { now as serverNow } from '~/models/clock'
import { undefinedEnglishCsvHeaderT } from '~/models/csvHeaderLanguage'

/**
 * CSV 1 列の定義。 headerKey は列名の i18n key、 accessor は行データから CSV セル値
 * (= 文字列) を取得する純関数。 enum / AreaCode 等は呼び出し側で既存の labels.ts /
 * i18n 経由の変換関数を通してから渡す (= コード値でなく表示名を出力する、design doc §5。
 * 区分値は表示言語で出す)。
 *
 * ヘッダーは列定義で訳さず key で持ち、 `buildCsvContent` が 1 か所で日本語に解決する。
 * 広域マスタの CSV は英語のヘッダー名が未定義なので、 表示言語に関わらずヘッダーを日本語で
 * 出す (`admin/master/occto-master/index.md` 機能詳細 5、 `conventions.md`「CSV の英語表示」)。
 */
export type CsvColumnDef<T> = {
  headerKey: string
  accessor: (row: T) => string
}

// Excel で開いた際に文字化けしないよう UTF-8 BOM を付与する
// (design doc §5「CSV 生成仕様」)。 ExecutionResultTab.tsx と同じ定数。
const BOM = '\uFEFF'

/**
 * 行データ + カラム定義から BOM 付き CSV 文字列を組み立てる (F1 純関数)。
 * 区切りはカンマ、改行は `\r\n` (RFC 4180、design doc §5)。
 * rows が空でもヘッダ行のみの CSV を返す (AC-06: 0 件フィルタ結果でも
 * ヘッダ行のみの CSV をダウンロードできる = テンプレートとして有用)。
 */
export function buildCsvContent<T>(
  rows: readonly T[],
  columnDefs: ReadonlyArray<CsvColumnDef<T>>,
): string {
  const th = undefinedEnglishCsvHeaderT()
  const headerLine = columnDefs
    .map((col) => escapeCsvCell(th(col.headerKey)))
    .join(',')
  const bodyLines = rows.map((row) =>
    columnDefs.map((col) => escapeCsvCell(col.accessor(row))).join(','),
  )
  return BOM + [headerLine, ...bodyLines].join('\r\n')
}

/**
 * ダウンロードファイル名 (design doc §5):
 * `occto-master_{tabKey}_{YYYY-MM-DD}.csv`
 */
export function csvFileName(tabKey: string, today: Date = serverNow()): string {
  const yyyy = today.getFullYear()
  const mm = String(today.getMonth() + 1).padStart(2, '0')
  const dd = String(today.getDate()).padStart(2, '0')
  return `occto-master_${tabKey}_${yyyy}-${mm}-${dd}.csv`
}

/**
 * CSV 生成 + Blob ダウンロードを実行する (SupplyGap/index.tsx /
 * ExecutionResultTab.tsx の既存 Blob + createObjectURL パターンを踏襲、
 * design doc §4「依存・前提」)。
 *
 * CSV の生成 (`buildCsvContent`) とダウンロード (Blob 生成 /
 * createObjectURL / クリック / revokeObjectURL) の両方を try-catch で
 * 囲み、失敗時は `onError` を呼ぶ (AC-03: 「CSV の生成またはダウンロードに
 * 失敗した場合」の両方をカバーする)。 toast 表示等の UI feedback は
 * 呼び出し側 (`useCsvExportHandler`) の責務とする (= モデル層は純粋に保つ、
 * `AdminApprovalFlow/models/saveProcess.ts` と同じ分離方針)。
 */
export function downloadCsv<T>(
  rows: readonly T[],
  columnDefs: ReadonlyArray<CsvColumnDef<T>>,
  filename: string,
  onError: () => void,
): void {
  try {
    const content = buildCsvContent(rows, columnDefs)
    downloadBlob(
      filename,
      new Blob([content], { type: 'text/csv;charset=utf-8;' }),
    )
  } catch {
    onError()
  }
}
